import { isReasonCode, reasonCopyKey } from 'components/Intelligence/reasonKey';

/**
 * PLA-8828: a display title for a governed reference, or `null` where the reference is an id and
 * nothing else.
 *
 * The Customer 360 wire names a detail row by `entity_ref`, and those refs are of two kinds. Most
 * are readable tokens the pipeline chose (`reward:cashback`, `campaign_interaction`,
 * `method:alias_exact`, `p2m`) and only need casing. A few are opaque handles
 * (`cmp_751c43bcf989c1bf`, the account half of `loyacct_ac5107a96156085a:balance`), and NOTHING on
 * this side resolves them: `GET /v4/dash/campaigns/cmp_751c43bcf989c1bf` answers `404` / `4044`
 * `Campaign ID is invalid`, because these are anonymised references from the governed source rather
 * than dashboard ids. A row named by one of those has no title, and inventing one ("Campaign 1")
 * would assert an identity mapping we do not have.
 *
 * So this returns `null` for them and the caller labels the row from something the wire actually
 * states, or drops it. The detection is SHAPE-based rather than a list of the refs one capture
 * happened to contain — a ref no one has seen yet is caught by the same rule.
 */

/** A hex run long enough to be a handle rather than a word — a uuid, a digest, a counter. */
const OPAQUE_HEX = /^[0-9a-f]{12,}$/;

/** Non-hex opaque alphabets (ULID, base32, base62) are caught by length plus digit density. */
const OPAQUE_MIN_LENGTH = 16;
const OPAQUE_MIN_DIGITS = 4;

const isOpaquePart = (part: string): boolean =>
  OPAQUE_HEX.test(part) ||
  (part.length >= OPAQUE_MIN_LENGTH && (part.match(/\d/g) || []).length >= OPAQUE_MIN_DIGITS);

/**
 * A whole `:` segment is opaque as soon as ONE of its `_`/`-` parts is: an id is routinely a short
 * kind prefix plus the handle (`cmp_…`, `loyacct_…`), and the prefix is not a title on its own.
 */
const isOpaqueSegment = (segment: string): boolean =>
  segment
    .split(/[_\-.]+/)
    .filter(Boolean)
    .some(isOpaquePart);

/** Words the sentence-caser leaves in capitals: short and vowelless is an acronym (`p2m`, `qr`). */
const ACRONYM_MAX_LENGTH = 4;
const VOWEL = /[aeiou]/;

/* Acronyms the vowel rule cannot see because they contain one -- `bfsi_savings` off O-COM-025 read
   as `Bfsi savings` on the propensity strip. Listed rather than inferred: a shorter-than-five word
   with a vowel is far more often a word than an acronym, so only a token the wire has actually been
   observed to use belongs here. PLA-8828. */
const KNOWN_ACRONYMS = new Set(['bfsi']);

const asWord = (word: string): string =>
  KNOWN_ACRONYMS.has(word) || (word.length <= ACRONYM_MAX_LENGTH && !VOWEL.test(word))
    ? word.toUpperCase()
    : word;

/**
 * Whether the ref's last `:` segment is an opaque handle -- `spend:cat_0903ba031210be44`, whose
 * `humaniseRef` reading is only the leading `Spend`, the KIND of thing and not which one. PLA-8828.
 */
export const endsOnOpaqueSegment = (ref: string | null | undefined): boolean => {
  const segments = typeof ref === 'string' ? ref.split(':').map(segment => segment.trim()) : [];
  const last = segments[segments.length - 1];

  return Boolean(last) && isOpaqueSegment(last);
};

/**
 * The ref as a title, or `null` when nothing readable survives.
 *
 * The LAST non-opaque `:` segment wins, because the leading segment is a namespace the row's own
 * `entity_type` already states (`reward:cashback` under `affinity_view_category`,
 * `method:alias_exact` under `resolution_profile`) — repeating it in the label says nothing the
 * panel does not. Sentence case, not title case: one capital per row reads as a label rather than
 * as a heading, and an acronym keeps its own capitals.
 */
export const humaniseRef = (ref: string | null | undefined): string | null => {
  if (typeof ref !== 'string') {
    return null;
  }

  const named = ref
    .split(':')
    .map(segment => segment.trim())
    .filter(segment => segment !== '' && !isOpaqueSegment(segment));

  const words = (named[named.length - 1] || '')
    .toLowerCase()
    .split(/[_\-.\s]+/)
    .filter(Boolean)
    .map(asWord);

  if (words.length === 0) {
    return null;
  }

  const [lead, ...rest] = words;
  const head = lead === lead.toUpperCase() ? lead : lead.charAt(0).toUpperCase() + lead.slice(1);

  return [head, ...rest].join(' ');
};

/**
 * A governed `value_state` as words, or `null` where the caller should keep what it already has.
 *
 * `reasonCopyKey` first — the rule `CustomerProfileSection` already reads `current_clv_band`
 * through: these states are a governed vocabulary, and their words are copy rather than casing.
 * `humaniseRef` behind it, because unlike a band drawn BESIDE a reading the state here IS the
 * reading, so a code the catalogue has no copy for must still arrive as words rather than as
 * `not_blocked`.
 *
 * Both steps are gated on the token being CODE-SHAPED, `reasonKey.ts`'s own rule: a supplier that
 * sends a sentence is sending prose, and running it through the sentence-caser re-cases display
 * text that was already correct — Contact Restrictions' warning-mark-plus-`Restricted` reading came
 * back with a lower-case `restricted`, the mark having been taken for the leading word.
 *
 * Lives here beside `humaniseRef` rather than in either section that calls it: Contact
 * Restrictions, Ready for Engagement and Customer Profile all resolve a state through it, and a
 * fourth slot should reach the same rule rather than a second copy of it. PLA-8828.
 */
export const governedStateLabel = (
  t: (key: string) => string,
  group: string,
  state: string | null | undefined
): string | null => {
  const key = reasonCopyKey(group, state);

  if (key) {
    return t(key);
  }

  return typeof state === 'string' && isReasonCode(state) ? humaniseRef(state) : null;
};

/**
 * A distribution segment's printable name: the wire's `display_name` where it states one, the
 * humanised key otherwise, `null` where neither names anything (an opaque `mrc_...` key alone).
 */
export const segmentName = (segment: { key: string; label?: string }): string | null =>
  humaniseRef(segment.label) ?? humaniseRef(segment.key);
